import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import '../stylings/DeliveryAreaCheck.css';

const FARMSTAND_ADDRESS = process.env.REACT_APP_FARMSTAND_ADDRESS || "4250 W 123rd St, Alsip, IL 60803";
const DELIVERY_RADIUS_MILES = parseFloat(process.env.REACT_APP_DELIVERY_RADIUS_MILES) || 7.5;
const DELIVERY_MINIMUM_ORDER = parseFloat(process.env.REACT_APP_DELIVERY_MINIMUM_ORDER) || 12.00;
const FREE_DELIVERY_THRESHOLD = parseFloat(process.env.REACT_APP_FREE_DELIVERY_THRESHOLD) || 50.00;

function DeliveryAreaCheck({ onAreaValidated, onBack }) {
  const { subtotal } = useCart();
  const [selectedOption, setSelectedOption] = useState('');
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: 'IL',
    zipCode: ''
  });
  
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [showMinimumWarning, setShowMinimumWarning] = useState(false);

  // Check if delivery is available based on order total
  const isDeliveryAvailable = subtotal >= DELIVERY_MINIMUM_ORDER;
  const remainingForDelivery = DELIVERY_MINIMUM_ORDER - subtotal;
  const remainingForFreeDelivery = FREE_DELIVERY_THRESHOLD - subtotal;
  const qualifiesForFreeDelivery = subtotal >= FREE_DELIVERY_THRESHOLD;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAddress(prev => ({
      ...prev,
      [name]: name === 'state' ? 'IL' : value
    }));
    setError('');
  };

  const formatAddress = (addr) => {
    return `${addr.street}, ${addr.city}, ${addr.state} ${addr.zipCode}`;
  };

  // Handle delivery selection with minimum order check
  const handleDeliverySelection = () => {
    if (!isDeliveryAvailable) {
      setShowMinimumWarning(true);
      return;
    }
    setSelectedOption('delivery');
    setShowMinimumWarning(false);
  };

  // Handle pickup selection
  const handlePickupSelection = () => {
    console.log('Customer selected farmstand pickup');
    setShowMap(true);
    
    setTimeout(() => {
      onAreaValidated(false, null, 'pickup');
    }, 1500);
  };

  const checkDeliveryArea = async () => {
    if (!address.street || !address.city || !address.zipCode) {
      setError('Please fill in all address fields');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!/^\d{5}$/.test(address.zipCode)) {
      setError('Please enter a valid 5-digit ZIP code');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsChecking(true);
    setError('');

    try {
      const userAddressString = formatAddress(address);
      
      const response = await fetch('https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-delivery-area-check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: userAddressString
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        if (result.error === 'Address not found') {
          throw new Error('Address not found. Please check your address and try again.');
        } else if (result.error === 'Address is required') {
          throw new Error('Please enter a complete address.');
        } else {
          throw new Error(result.error || 'Failed to check delivery area');
        }
      }

      const isInArea = result.isInDeliveryArea;
      
      console.log(`Address "${userAddressString}" check result: ${isInArea ? 'In' : 'Outside'} delivery area`);
      
      setShowMap(true);
      
      setTimeout(() => {
        onAreaValidated(isInArea, address, 'delivery');
      }, 2000);

    } catch (error) {
      console.error('Delivery area check error:', error);
      setError(error.message || 'Unable to verify address. Please check your address and try again.');
      setIsChecking(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const generateStaticMapUrl = () => {
    const farmstandCoords = "41.6436,-87.7394";
    return `https://www.openstreetmap.org/export/embed.html?bbox=-87.8,-41.7,-87.65,-41.6&layer=mapnik&marker=${farmstandCoords}`;
  };

  return (
    <div className="delivery-area-check">
      <div className="delivery-check-header">
        <h2>Choose Your Order Method</h2>
        <p>Select how you'd like to receive your fresh farm products</p>
      </div>

      {!selectedOption && !showMap && (
        <div className="method-selection">
          {/* Show minimum order warning if triggered */}
          {showMinimumWarning && (
            <div className="minimum-order-warning">
              <div className="warning-icon">⚠️</div>
              <div className="warning-content">
                <h3>Minimum Order Required for Delivery</h3>
                <p>
                  Delivery requires a minimum order of <strong>${DELIVERY_MINIMUM_ORDER.toFixed(2)}</strong> in produce. 
                  Your current order total is <strong>${subtotal.toFixed(2)}</strong>.
                </p>
                <p>
                  You need <strong>${remainingForDelivery.toFixed(2)}</strong> more to qualify for delivery.
                </p>
                <div className="warning-actions">
                  <button 
                    className="add-items-btn" 
                    onClick={() => window.location.href = '/shop'}
                  >
                    Add More Items
                  </button>
                  <button 
                    className="choose-pickup-btn" 
                    onClick={() => setShowMinimumWarning(false)}
                  >
                    Choose Pickup Instead
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Show free delivery promotion if close to threshold */}
          {isDeliveryAvailable && !qualifiesForFreeDelivery && remainingForFreeDelivery <= 20 && (
            <div className="free-delivery-promotion">
              <div className="promotion-icon">🚚</div>
              <div className="promotion-content">
                <h3>You're Close to Free Delivery!</h3>
                <p>
                  Add <strong>${remainingForFreeDelivery.toFixed(2)}</strong> more to your order and get 
                  <strong> FREE delivery</strong> (orders $${FREE_DELIVERY_THRESHOLD.toFixed(2)}+)
                </p>
                <button 
                  className="add-more-btn" 
                  onClick={() => window.location.href = '/shop'}
                >
                  Add More Items
                </button>
              </div>
            </div>
          )}

          {/* Show free delivery achievement if qualified */}
          {qualifiesForFreeDelivery && (
            <div className="free-delivery-achieved">
              <div className="achievement-icon">🎉</div>
              <div className="achievement-content">
                <h3>Congratulations! You Qualify for Free Delivery</h3>
                <p>
                  Your order of <strong>${subtotal.toFixed(2)}</strong> qualifies for 
                  <strong> FREE delivery</strong> (orders $${FREE_DELIVERY_THRESHOLD.toFixed(2)}+)
                </p>
              </div>
            </div>
          )}

          <div className="method-options">
            <div 
              className={`method-option delivery-option ${!isDeliveryAvailable ? 'minimum-not-met' : ''}`} 
              onClick={handleDeliverySelection}
            >
              <div className="option-icon">🚚</div>
              <div className="option-content">
                <h3>Home Delivery</h3>
                <p>We'll bring fresh produce to your door</p>
                <div className="option-details">
                  <span className="detail-item">📍 Within {DELIVERY_RADIUS_MILES} miles of our farmstand</span>
                  <span className="detail-item">
                    💰 {qualifiesForFreeDelivery 
                      ? 'FREE delivery for your order!' 
                      : `$8.99 delivery fee (free on orders $${FREE_DELIVERY_THRESHOLD.toFixed(2)}+)`
                    }
                  </span>
                  <span className="detail-item">⏰ Delivered by 3:30 PM next day</span>
                  <span className="detail-item minimum-requirement">
                    📦 Minimum order: ${DELIVERY_MINIMUM_ORDER.toFixed(2)}
                  </span>
                </div>
                <button className={`option-button delivery-btn ${!isDeliveryAvailable ? 'disabled' : ''}`}>
                  {isDeliveryAvailable ? 'Check Delivery Area' : `Need $${remainingForDelivery.toFixed(2)} More`}
                </button>
              </div>
              {!isDeliveryAvailable && (
                <div className="minimum-badge">
                  <span className="badge-text">Minimum Not Met</span>
                </div>
              )}
              {qualifiesForFreeDelivery && isDeliveryAvailable && (
                <div className="free-delivery-badge">
                  <span className="badge-text">Free Delivery!</span>
                </div>
              )}
            </div>

            <div className="method-option pickup-option" onClick={handlePickupSelection}>
              <div className="option-icon">🏪</div>
              <div className="option-content">
                <h3>Farmstand Pickup</h3>
                <p>Pick up your order at our farmstand</p>
                <div className="option-details">
                  <span className="detail-item">📍 {FARMSTAND_ADDRESS}</span>
                  <span className="detail-item">💰 Always free pickup</span>
                  <span className="detail-item">⏰ Ready for pickup by 3:30 PM next day</span>
                  <span className="detail-item no-minimum">📦 No minimum order required</span>
                </div>
                <button className="option-button pickup-btn">
                  Choose Pickup
                </button>
              </div>
              <div className="pickup-badge">
                <span className="badge-text">{!isDeliveryAvailable ? 'Recommended' : 'Most Popular'}</span>
              </div>
            </div>
          </div>

          <div className="method-info">
            <div className="info-card">
              <h4>🌱 Farm Fresh Guarantee</h4>
              <p>All orders are picked fresh the morning of delivery or pickup. We guarantee the highest quality produce from our local trusted farm.</p>
            </div>
            
            {!isDeliveryAvailable && (
              <div className="info-card minimum-info">
                <h4>📦 Delivery Minimum Order</h4>
                <p>
                  To ensure efficient delivery and fresh produce, we require a minimum order of ${DELIVERY_MINIMUM_ORDER.toFixed(2)} for delivery service. 
                  Pickup orders have no minimum requirement.
                </p>
              </div>
            )}

            {isDeliveryAvailable && !qualifiesForFreeDelivery && (
              <div className="info-card free-delivery-info">
                <h4>🚚 Free Delivery Available</h4>
                <p>
                  Get FREE delivery on orders of $${FREE_DELIVERY_THRESHOLD.toFixed(2)} or more! 
                  You're only $${remainingForFreeDelivery.toFixed(2)} away from free delivery.
                </p>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="back-btn" onClick={onBack}>
              Back to Basket
            </button>
          </div>
        </div>
      )}

      {selectedOption === 'delivery' && !showMap && (
        <div className="address-form">
          <div className="form-section">
            <div className="selected-method-header">
              <div className="method-indicator">
                <span className="method-icon">🚚</span>
                <div className="method-text">
                  <h3>Home Delivery Selected</h3>
                  <p>Enter your address to check if we can deliver to you</p>
                  <p className="minimum-met">✅ Minimum order requirement met (${DELIVERY_MINIMUM_ORDER.toFixed(2)})</p>
                  {qualifiesForFreeDelivery && (
                    <p className="free-delivery-met">🚚 FREE delivery qualified! (${FREE_DELIVERY_THRESHOLD.toFixed(2)}+)</p>
                  )}
                </div>
                <button 
                  className="change-method-btn" 
                  onClick={() => setSelectedOption('')}
                  type="button"
                >
                  Change Method
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="street">Street Address*</label>
              <input
                type="text"
                id="street"
                name="street"
                value={address.street}
                onChange={handleInputChange}
                placeholder="123 Main Street"
                required
              />
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label htmlFor="city">City*</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={address.city}
                  onChange={handleInputChange}
                  placeholder="Chicago"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="state">State*</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value="Illinois"
                  disabled
                  className="disabled-input"
                  style={{
                    backgroundColor: '#f8f9fa',
                    color: '#6c757d',
                    cursor: 'not-allowed'
                  }}
                />
              </div>
              <div className="form-group">
                <label htmlFor="zipCode">ZIP Code*</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={address.zipCode}
                  onChange={handleInputChange}
                  placeholder="60603"
                  pattern="[0-9]{5}"
                  maxLength="5"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="delivery-info">
              <div className="info-card">
                <h4>📍 Our Farmstand Location</h4>
                <p>{FARMSTAND_ADDRESS}</p>
                <p className="radius-info">Delivery available within {DELIVERY_RADIUS_MILES} miles</p>
                <p className="pickup-info">🅿️ Free pickup always available at our farmstand</p>
                <p className="timing-info">⏰ All deliveries and pickups completed by 3:30 PM</p>
                <p className="free-delivery-info">🚚 Free delivery on orders $${FREE_DELIVERY_THRESHOLD.toFixed(2)}+</p>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="back-btn" onClick={() => setSelectedOption('')}>
              Back to Methods
            </button>
            <button 
              type="button" 
              className="check-btn" 
              onClick={checkDeliveryArea}
              disabled={isChecking}
            >
              {isChecking ? 'Checking Area...' : 'Check Delivery Area'}
            </button>
          </div>
        </div>
      )}

      {showMap && (
        <div className="checking-result">
          <div className="checking-spinner">
            <div className="spinner"></div>
          </div>
          <h3>
            {selectedOption === 'pickup' || selectedOption === '' 
              ? 'Confirming pickup selection...' 
              : 'Checking your delivery area...'
            }
          </h3>
          <p>
            {selectedOption === 'pickup' || selectedOption === ''
              ? 'Setting up your farmstand pickup order'
              : 'Please wait while we verify your location'
            }
          </p>
          
          <div className="map-container">
            <iframe
              src={generateStaticMapUrl()}
              width="100%"
              height="300"
              style={{ border: 0, borderRadius: '12px' }}
              title="Delivery Area Map"
            />
            <div className="map-overlay">
              <div className="farmstand-marker">
                <span className="marker-icon">🏪</span>
                <span className="marker-label">Buck's Farmstand</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryAreaCheck;